import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader, Table, Btn, Select, Field } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

interface TransferRow {
  _id: string;
  reference: string;
  status: 'pending' | 'paid' | 'cancelled' | 'expired' | 'failed';
  concept: 'ORDER' | 'WALLET_TOPUP';
  method: string;
  amount: number;
  walletAmount: number;
  currency: string;
  createdAt: string;
  orderId?: string | null;
  orderIds?: string[];
  user?: { _id: string; email: string; firstName: string; lastName: string };
}

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'paid', label: 'Pagada' },
  { value: 'cancelled', label: 'Cancelada' },
  { value: 'expired', label: 'Expirada' },
  { value: 'failed', label: 'Fallida' },
];
const CONCEPT_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'ORDER', label: 'Orden' },
  { value: 'WALLET_TOPUP', label: 'Recarga wallet' },
];

function StatusPill({ s }: { s: TransferRow['status'] }) {
  const palette =
    s === 'paid'
      ? { bg: 'color-mix(in oklab, var(--accent) 14%, transparent)', fg: 'var(--accent)' }
      : s === 'pending'
      ? { bg: 'color-mix(in oklab, oklch(0.78 0.16 70) 14%, transparent)', fg: 'oklch(0.78 0.16 70)' }
      : { bg: 'color-mix(in oklab, var(--danger) 12%, transparent)', fg: 'var(--danger)' };
  return (
    <span className="mono" style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 999, background: palette.bg, color: palette.fg, fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
      {s}
    </span>
  );
}

export default function TransfersPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') ?? '';
  const concept = sp.get('concept') ?? '';
  const search = sp.get('search') ?? '';
  const page = parseInt(sp.get('page') ?? '1', 10);

  const setFilter = (next: Partial<{ status: string; concept: string; search: string; page: string }>) => {
    const merged: Record<string, string> = {};
    const cur = { status, concept, search, page: String(page) };
    for (const k of Object.keys(cur) as (keyof typeof cur)[]) {
      const v = next[k] !== undefined ? next[k] : cur[k];
      if (v && v !== '1') merged[k] = v as string;
      else if (k === 'page' && v && v !== '1') merged[k] = v as string;
    }
    setSp(merged);
  };

  const { data, isLoading } = useQuery<{ items: TransferRow[]; total: number; page: number; limit: number }>({
    queryKey: ['admin', 'transfers', status, concept, search, page],
    queryFn: () => api('/api/admin/bank-transfers', { query: { status: status || undefined, concept: concept || undefined, search: search || undefined, page } }),
    refetchInterval: 30_000,
  });

  const markPaid = useMutation({
    mutationFn: (reference: string) => api(`/api/admin/bank-transfers/${reference}/mark-paid`, { body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'transfers'] }),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <div>
      <PageHeader
        kicker="TRANSFERENCIAS"
        title={`${data?.total ?? 0} ${data?.total === 1 ? 'transferencia' : 'transferencias'}`}
        sub="Conciliación manual contra el extracto bancario. Buscá por referencia, email o nombre."
      />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 180 }}>
          <Select<string> label="Estado" value={status} onChange={(v) => setFilter({ status: v, page: '1' })} options={STATUS_OPTIONS} />
        </div>
        <div style={{ minWidth: 180 }}>
          <Select<string> label="Tipo" value={concept} onChange={(v) => setFilter({ concept: v, page: '1' })} options={CONCEPT_OPTIONS} />
        </div>
        <div style={{ minWidth: 260, flex: 1 }}>
          <Field label="Buscar" value={search} onChange={(v) => setFilter({ search: v, page: '1' })} placeholder="CKT-…, email, nombre" />
        </div>
      </div>

      <Table<TransferRow>
        rows={data?.items ?? []}
        empty={isLoading ? 'Cargando…' : 'Sin transferencias.'}
        columns={[
          {
            key: 'reference',
            label: 'Referencia',
            render: (r) => (
              <Link to={`/transfers/${r.reference}`} className="mono" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none', fontSize: 12 }}>
                {r.reference}
              </Link>
            ),
          },
          {
            key: 'user',
            label: 'Usuario',
            render: (r) =>
              r.user ? (
                <Link to={`/users/${r.user._id}`} style={{ color: 'var(--text)', textDecoration: 'none' }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{r.user.firstName} {r.user.lastName}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{r.user.email}</div>
                </Link>
              ) : (
                <span style={{ color: 'var(--text-3)' }}>—</span>
              ),
          },
          {
            key: 'concept',
            label: 'Tipo',
            render: (r) => <span className="mono" style={{ fontSize: 11, color: 'var(--text-2)' }}>{r.concept === 'WALLET_TOPUP' ? 'RECARGA' : 'ORDEN'}</span>,
          },
          {
            key: 'amount',
            label: 'Monto',
            render: (r) => (
              <div>
                <span className="mono" style={{ fontWeight: 600 }}>${r.amount.toFixed(2)} {r.currency}</span>
                {r.walletAmount > 0 && (
                  <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>+ ${r.walletAmount.toFixed(2)} de wallet</div>
                )}
              </div>
            ),
          },
          { key: 'status', label: 'Estado', render: (r) => <StatusPill s={r.status} />, width: 110 },
          {
            key: 'created',
            label: 'Creada',
            render: (r) => (
              <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }} title={new Date(r.createdAt).toLocaleString('es-MX')}>
                {new Date(r.createdAt).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            ),
          },
          {
            key: 'action',
            label: '',
            render: (r) =>
              r.status === 'pending' && can('transfers.edit') ? (
                <Btn
                  size="sm"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (confirm(`Confirmar recepción de $${r.amount.toFixed(2)} ${r.currency} con referencia ${r.reference}?`)) {
                      markPaid.mutate(r.reference);
                    }
                  }}
                  disabled={markPaid.isPending}
                >
                  Marcar recibida
                </Btn>
              ) : (
                <Link to={`/transfers/${r.reference}`} style={{ color: 'var(--text-2)', fontSize: 12, textDecoration: 'none' }}>
                  Ver →
                </Link>
              ),
          },
        ]}
      />

      {data && totalPages > 1 && (
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {data.total} totales · página {data.page} de {totalPages}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button disabled={page <= 1} onClick={() => setFilter({ page: String(page - 1) })} style={pager(page <= 1)}>←</button>
            <button disabled={page >= totalPages} onClick={() => setFilter({ page: String(page + 1) })} style={pager(page >= totalPages)}>→</button>
          </div>
        </div>
      )}
    </div>
  );
}

function pager(disabled: boolean): React.CSSProperties {
  return {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    color: disabled ? 'var(--text-3)' : 'var(--text)',
    width: 32, height: 32, borderRadius: 8,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit',
  };
}
